import React, { useState, useRef, useEffect, useCallback } from 'react';

const AVAILABLE_PARTICLES = [
  { id: 'e-', symbol: 'e⁻', name: 'Electron', color: '#50e0a0', charge: -1, lepton: 1, baryon: 0, type: 'fermion' },
  { id: 'e+', symbol: 'e⁺', name: 'Positron', color: '#50e0a0', charge: +1, lepton: -1, baryon: 0, type: 'fermion' },
  { id: 'mu-', symbol: 'μ⁻', name: 'Muon', color: '#50e0a0', charge: -1, lepton: 1, baryon: 0, type: 'fermion' },
  { id: 'p', symbol: 'p', name: 'Proton', color: '#ff9040', charge: +1, lepton: 0, baryon: 1, type: 'hadron' },
  { id: 'n', symbol: 'n', name: 'Neutron', color: '#ff9040', charge: 0, lepton: 0, baryon: 1, type: 'hadron' },
  { id: 'gamma', symbol: 'γ', name: 'Photon', color: '#ffffff', charge: 0, lepton: 0, baryon: 0, type: 'boson' },
  { id: 'Z', symbol: 'Z⁰', name: 'Z boson', color: '#ff9040', charge: 0, lepton: 0, baryon: 0, type: 'boson' },
  { id: 'W+', symbol: 'W⁺', name: 'W⁺ boson', color: '#ff9040', charge: +1, lepton: 0, baryon: 0, type: 'boson' },
  { id: 'W-', symbol: 'W⁻', name: 'W⁻ boson', color: '#ff9040', charge: -1, lepton: 0, baryon: 0, type: 'boson' },
  { id: 'H', symbol: 'H⁰', name: 'Higgs', color: '#f0c040', charge: 0, lepton: 0, baryon: 0, type: 'boson' },
  { id: 'nu_e', symbol: 'νₑ', name: 'Electron neutrino', color: '#50e0a080', charge: 0, lepton: 1, baryon: 0, type: 'fermion' },
  { id: 'g', symbol: 'g', name: 'Gluon', color: '#e060ff', charge: 0, lepton: 0, baryon: 0, type: 'boson' },
];

const KNOWN_VERTICES = [
  { particles: ['e-', 'e+', 'gamma'], label: 'QED vertex', valid: true, note: 'Electron-positron-photon: the fundamental QED interaction' },
  { particles: ['e-', 'gamma', 'e-'], label: 'Compton scatter', valid: true, note: 'Electron absorbs/emits a photon' },
  { particles: ['e-', 'e+', 'Z'], label: 'Neutral current', valid: true, note: 'Electron-positron annihilation via Z boson' },
  { particles: ['e-', 'nu_e', 'W-'], label: 'Charged current', valid: true, note: 'Beta decay vertex — electron + neutrino + W⁻' },
  { particles: ['p', 'n', 'W+'], label: 'Beta decay', valid: true, note: 'Proton + neutron interact via W⁺ — nuclear beta decay' },
  { particles: ['e-', 'e+', 'H'], label: 'Higgs coupling', valid: true, note: 'Electron-Higgs coupling vertex — mass generation' },
  { particles: ['gamma', 'gamma', 'H'], label: 'H→γγ loop', valid: true, note: 'Higgs to two photons via quantum loop (virtual top quarks/W)' },
  { particles: ['g', 'g', 'H'], label: 'Gluon fusion', valid: true, note: 'Main Higgs production mode at LHC — gluon-gluon fusion via top quark loop' },
];

function checkConservation(placed) {
  if (placed.length < 2) return null;
  const totalCharge = placed.reduce((s, p) => s + (p.incoming ? p.charge : -p.charge), 0);
  const totalLepton = placed.reduce((s, p) => s + (p.incoming ? p.lepton : -p.lepton), 0);
  const totalBaryon = placed.reduce((s, p) => s + (p.incoming ? p.baryon : -p.baryon), 0);
  return { charge: totalCharge, lepton: totalLepton, baryon: totalBaryon, valid: totalCharge === 0 && totalLepton === 0 && totalBaryon === 0 };
}

function findMatchingVertex(placed) {
  if (placed.length < 2) return null;
  const ids = placed.map(p => p.id).sort();
  return KNOWN_VERTICES.find(v => {
    const vs = [...v.particles].sort();
    return vs.length === ids.length && vs.every((p, i) => p === ids[i]);
  });
}

export default function FeynmanBuilder({ mode }) {
  const [placed, setPlaced] = useState([]);
  const [dragOver, setDragOver] = useState(false);
  const [incomingIds, setIncomingIds] = useState(new Set());
  const canvasRef = useRef(null);
  const animRef = useRef(null);
  const isRuach = mode === 'ruach';
  const accentColor = isRuach ? '#ffb060' : '#f0c040';

  const conservation = checkConservation(placed.map(p => ({ ...p, incoming: incomingIds.has(p.uid) })));
  const matchedVertex = findMatchingVertex(placed);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    const W = canvas.width, H = canvas.height;
    let t = 0;

    const draw = () => {
      t += 0.015;
      ctx.clearRect(0, 0, W, H);
      const cx = W / 2, cy = H / 2;

      if (placed.length === 0) {
        ctx.font = '12px Space Mono, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.15)';
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(isRuach ? 'Drag particles to build an interaction' : 'Drag particles onto the canvas', cx, cy - 10);
        ctx.font = '10px Space Mono, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.08)';
        ctx.fillText(isRuach ? 'the Feynman diagram — the vertex of meeting' : 'Feynman diagram builder', cx, cy + 10);
        animRef.current = requestAnimationFrame(draw);
        return;
      }

      // Draw vertex point
      const pulse = 0.7 + 0.3 * Math.sin(t * 2);
      ctx.beginPath(); ctx.arc(cx, cy, 10 * pulse, 0, Math.PI * 2);
      ctx.fillStyle = conservation?.valid ? '#50e0a030' : 'rgba(255,100,100,0.2)';
      ctx.fill();
      ctx.beginPath(); ctx.arc(cx, cy, 5, 0, Math.PI * 2);
      ctx.fillStyle = conservation?.valid ? '#50e0a0' : '#ff6060';
      ctx.fill();

      // Draw particle lines
      placed.forEach((p, i) => {
        const angle = (i / placed.length) * Math.PI * 2 - Math.PI / 2;
        const isIn = incomingIds.has(p.uid);
        const lineLen = 70;
        const x2 = cx + Math.cos(angle) * lineLen;
        const y2 = cy + Math.sin(angle) * lineLen;

        // Line style
        ctx.beginPath();
        if (p.type === 'boson') {
          // Wavy line for bosons
          const steps = 12;
          ctx.moveTo(cx, cy);
          for (let s = 0; s <= steps; s++) {
            const frac = s / steps;
            const lx = cx + Math.cos(angle) * lineLen * frac;
            const ly = cy + Math.sin(angle) * lineLen * frac;
            const perpX = -Math.sin(angle) * Math.sin(s * Math.PI) * 5;
            const perpY = Math.cos(angle) * Math.sin(s * Math.PI) * 5;
            if (s === 0) ctx.moveTo(lx + perpX, ly + perpY);
            else ctx.lineTo(lx + perpX, ly + perpY);
          }
        } else {
          // Straight line for fermions
          ctx.moveTo(cx, cy); ctx.lineTo(x2, y2);
        }
        ctx.strokeStyle = p.color;
        ctx.lineWidth = 1.5; ctx.stroke();

        // Arrow for fermions
        if (p.type !== 'boson') {
          const arrowX = isIn ? cx + Math.cos(angle) * lineLen * 0.5 : x2;
          const arrowY = isIn ? cy + Math.sin(angle) * lineLen * 0.5 : y2;
          const arrowDir = isIn ? -1 : 1;
          ctx.beginPath();
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(arrowX - arrowDir * Math.cos(angle) * 8 - Math.sin(angle) * 5, arrowY - arrowDir * Math.sin(angle) * 8 + Math.cos(angle) * 5);
          ctx.moveTo(arrowX, arrowY);
          ctx.lineTo(arrowX - arrowDir * Math.cos(angle) * 8 + Math.sin(angle) * 5, arrowY - arrowDir * Math.sin(angle) * 8 - Math.cos(angle) * 5);
          ctx.strokeStyle = p.color; ctx.lineWidth = 1.2; ctx.stroke();
        }

        // Particle label
        const labelX = cx + Math.cos(angle) * (lineLen + 14);
        const labelY = cy + Math.sin(angle) * (lineLen + 14);
        ctx.font = 'bold 11px Space Mono, monospace';
        ctx.fillStyle = p.color;
        ctx.textAlign = 'center'; ctx.textBaseline = 'middle';
        ctx.fillText(p.symbol, labelX, labelY);

        // In/out label
        ctx.font = '8px Space Mono, monospace';
        ctx.fillStyle = 'rgba(255,255,255,0.3)';
        ctx.fillText(isIn ? 'in' : 'out', labelX, labelY + 13);
      });

      animRef.current = requestAnimationFrame(draw);
    };

    animRef.current = requestAnimationFrame(draw);
    return () => cancelAnimationFrame(animRef.current);
  }, [placed, incomingIds, conservation, isRuach]);

  const addParticle = (p) => {
    const uid = Date.now() + Math.random();
    setPlaced(prev => [...prev, { ...p, uid }]);
    setIncomingIds(prev => new Set([...prev, uid]));
  };

  const removeParticle = (uid) => {
    setPlaced(prev => prev.filter(p => p.uid !== uid));
    setIncomingIds(prev => { const n = new Set(prev); n.delete(uid); return n; });
  };

  const toggleDirection = (uid) => {
    setIncomingIds(prev => {
      const n = new Set(prev);
      if (n.has(uid)) n.delete(uid); else n.add(uid);
      return n;
    });
  };

  const reset = () => { setPlaced([]); setIncomingIds(new Set()); };

  return (
    <div>
      <p style={{ color: 'rgba(255,255,255,0.45)', fontSize: '0.75rem', letterSpacing: '0.1em', marginBottom: '1.25rem', fontFamily: 'Space Mono', textTransform: 'uppercase' }}>
        {isRuach ? 'Build a vertex — the moment of interaction' : 'Build a Feynman diagram vertex'}
      </p>

      {/* Particle palette */}
      <div style={{ marginBottom: '1.25rem' }}>
        <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.5rem' }}>
          {isRuach ? 'Click to add a particle to the vertex' : 'Click particles to add to diagram'}
        </div>
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
          {AVAILABLE_PARTICLES.map(p => (
            <button key={p.id} onClick={() => addParticle(p)} style={{
              padding: '4px 10px', borderRadius: '6px', cursor: 'pointer',
              background: `${p.color}15`, border: `1px solid ${p.color}40`,
              color: p.color, fontFamily: 'Space Mono', fontSize: '0.75rem',
              transition: 'all 0.12s',
            }}>
              {p.symbol}
            </button>
          ))}
        </div>
      </div>

      {/* Canvas */}
      <div
        onDragOver={e => { e.preventDefault(); setDragOver(true); }}
        onDragLeave={() => setDragOver(false)}
        onDrop={e => { e.preventDefault(); setDragOver(false); }}
        style={{
          borderRadius: '12px', overflow: 'hidden', marginBottom: '1rem',
          border: `1px solid ${dragOver ? accentColor + '60' : 'rgba(255,255,255,0.08)'}`,
          background: 'rgba(0,0,0,0.3)', transition: 'border-color 0.2s',
        }}
      >
        <canvas ref={canvasRef} width={320} height={240}
          style={{ width: '100%', maxWidth: '320px', display: 'block', margin: '0 auto' }} />
      </div>

      {/* Placed particles list */}
      {placed.length > 0 && (
        <div style={{ marginBottom: '1rem' }}>
          <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.25)', marginBottom: '0.5rem' }}>
            Particles at vertex — click direction to toggle in/out
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px' }}>
            {placed.map(p => (
              <div key={p.uid} style={{ display: 'flex', alignItems: 'center', gap: '4px', background: `${p.color}12`, border: `1px solid ${p.color}35`, borderRadius: '6px', padding: '3px 6px' }}>
                <span style={{ fontFamily: 'Space Mono', fontSize: '0.75rem', color: p.color }}>{p.symbol}</span>
                <button onClick={() => toggleDirection(p.uid)} style={{
                  background: 'none', border: 'none', cursor: 'pointer', padding: '0 2px',
                  color: incomingIds.has(p.uid) ? '#50e0a0' : '#ff9040', fontSize: '0.65rem', fontFamily: 'Space Mono',
                }}>
                  {incomingIds.has(p.uid) ? '→in' : '←out'}
                </button>
                <button onClick={() => removeParticle(p.uid)} style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'rgba(255,255,255,0.3)', fontSize: '0.8rem', padding: '0 2px' }}>×</button>
              </div>
            ))}
            <button onClick={reset} style={{ padding: '3px 8px', borderRadius: '6px', background: 'none', border: '1px solid rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.3)', fontFamily: 'Space Mono', fontSize: '0.68rem', cursor: 'pointer' }}>
              Clear
            </button>
          </div>
        </div>
      )}

      {/* Conservation laws */}
      {conservation && (
        <div style={{ marginBottom: '1rem', padding: '0.875rem 1rem', borderRadius: '8px', border: `1px solid ${conservation.valid ? 'rgba(80,224,160,0.3)' : 'rgba(255,100,100,0.3)'}`, background: conservation.valid ? 'rgba(80,224,160,0.06)' : 'rgba(255,100,100,0.06)' }}>
          <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.08em', color: conservation.valid ? '#50e0a0' : '#ff6060', marginBottom: '0.5rem' }}>
            {conservation.valid ? '✓ Conservation laws satisfied' : '✗ Conservation laws violated'}
          </div>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            {[
              { label: 'Charge', val: conservation.charge },
              { label: 'Lepton #', val: conservation.lepton },
              { label: 'Baryon #', val: conservation.baryon },
            ].map(c => (
              <div key={c.label} style={{ fontSize: '0.72rem', fontFamily: 'Space Mono' }}>
                <span style={{ color: 'rgba(255,255,255,0.35)' }}>{c.label}: </span>
                <span style={{ color: c.val === 0 ? '#50e0a0' : '#ff6060' }}>{c.val === 0 ? '0 ✓' : `${c.val > 0 ? '+' : ''}${c.val} ✗`}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Matched vertex */}
      {matchedVertex && (
        <div style={{ padding: '0.875rem 1rem', borderRadius: '8px', border: `1px solid ${accentColor}35`, background: `${accentColor}08` }}>
          <div style={{ fontSize: '0.65rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.08em', color: accentColor, marginBottom: '0.3rem' }}>
            {isRuach ? 'Known interaction identified' : 'Known vertex identified'}
          </div>
          <div style={{ fontSize: '0.9rem', fontWeight: 600, color: '#fff', fontFamily: 'Syne', marginBottom: '0.3rem' }}>{matchedVertex.label}</div>
          <p style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.55)', lineHeight: 1.65, margin: 0 }}>{matchedVertex.note}</p>
        </div>
      )}

      {/* Known vertices reference */}
      <div style={{ marginTop: '1.25rem' }}>
        <div style={{ fontSize: '0.62rem', fontFamily: 'Space Mono', textTransform: 'uppercase', letterSpacing: '0.1em', color: 'rgba(255,255,255,0.2)', marginBottom: '0.6rem' }}>
          {isRuach ? 'Known vertices to try' : 'Known vertices to build'}
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
          {KNOWN_VERTICES.map(v => (
            <div key={v.label} onClick={() => { reset(); v.particles.forEach(id => { const p = AVAILABLE_PARTICLES.find(x => x.id === id); if (p) addParticle(p); }); }} style={{
              display: 'flex', alignItems: 'center', gap: '0.75rem', padding: '5px 8px',
              borderRadius: '6px', cursor: 'pointer', background: 'rgba(255,255,255,0.02)',
              border: '1px solid rgba(255,255,255,0.05)', transition: 'background 0.12s',
            }}>
              <div style={{ display: 'flex', gap: '3px' }}>
                {v.particles.map((pid, i) => {
                  const p = AVAILABLE_PARTICLES.find(x => x.id === pid);
                  return <span key={i} style={{ fontFamily: 'Space Mono', fontSize: '0.7rem', color: p?.color || '#fff' }}>{p?.symbol}</span>;
                })}
              </div>
              <span style={{ fontSize: '0.75rem', color: 'rgba(255,255,255,0.45)', flex: 1 }}>{v.label}</span>
              <span style={{ fontSize: '0.65rem', color: 'rgba(255,255,255,0.2)', fontFamily: 'Space Mono' }}>try →</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
